import React, { useContext, useState } from 'react';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { RichText } from 'src/components/RichText/RichText';
import { FeaturedProducts } from 'src/components/IndividualPage/FeaturedProducts';
import { CategoryQueryContext } from 'src/components/CategoryListingPage/CategoryQueryProvider';
import classnames from 'classnames';
import { ImageAndHoverText } from 'src/elements/ImageAndHoverText/ImageAndHoverText';
import classes from 'src/components/CategoryList/CategoryList.scss';
import { useAggregationsByCategoryId } from 'src/components/CategoryListingPage/AggregationQueryProvider';
import { Carousel } from 'src/elements/Carousel/Carousel';
import { IndustryCategories } from 'src/components/IndustryCategories/IndustryCategories';

import { BrandItem, FaqItem } from '../CategoryLandingPage/CategoryLandingPage';
import { CategoryFaq } from '../CategoryFaq/CategoryFaq';
import { MobileCreditBalance } from '../Customer/MobileCreditBalance';
export interface CategoryItem {
    image: string;
    link: string;
    title: string;
}
type CategoriesListtProps = {
    title: string;
    titleElem?: 'h1' | 'h2';
    categories: CategoryItem[];
    richText?: string | null;
    shortDescription?: string | null;
    featuredSku?: string | null;
    extendedDescription?: string | null;
    displayHeader?: boolean;
    categoryId?: number | null;
    brands: BrandItem[];
    relatedCategories: string[];
    featuredPrintedSku?: string | null;
    featuredEmbroideredSku?: string | null;
    faq: FaqItem[];
    displayFullView: boolean;
};

const Tags = ({ categories, title }: { categories: CategoryItem[]; title: string }) => (
    <div className={classes.tags}>
        {categories.length > -1 &&
            categories.map((category) => {
                let categoryTitle = category.title;
                title.split(' ').forEach((word) => {
                    if (categoryTitle.includes(word)) {
                        categoryTitle = categoryTitle.split(word).join('').trim();
                    }
                });
                if (categoryTitle !== '') {
                    return (
                        <a key={category.title} className={classes.tag} href={category.link}>
                            {categoryTitle.split('&').join('').split(' s').join('')}
                        </a>
                    );
                }
            })}
    </div>
);

export const CategoriesList: React.FC<CategoriesListtProps> = ({
    title,
    categories,
    shortDescription,
    featuredSku,
    extendedDescription,
    displayHeader = false,
    categoryId,
    brands,
    relatedCategories,
    featuredPrintedSku,
    featuredEmbroideredSku,
    faq,
    displayFullView,
}) => {
    const { urlKey, name, bestSellers, breadcrumbLabel } = useContext(CategoryQueryContext);
    const SKUS = featuredSku?.split(',').map((value) => value.trim().replace(/'/g, ''));
    const printedSKUS = featuredPrintedSku?.split(',').map((value) => value.trim().replace(/'/g, ''));
    const embroideredSKUS = featuredEmbroideredSku?.split(',').map((value) => value.trim().replace(/'/g, ''));

    const [additionalSkuType, setAdditionalSkuType] = useState<'screenPrintedSkus' | 'embroideredSkus'>(
        'screenPrintedSkus',
    );

    const { tagsAggregation } = useAggregationsByCategoryId(categoryId ? categoryId.toString() : '');

    if (typeof document !== 'undefined') {
        const html = extendedDescription || '';
        const doc = document.implementation.createHTMLDocument('');
        doc.documentElement.innerHTML = html;
    }

    const [readMore, setReadMore] = useState(false);

    const allCategoryLink = categories.find((category) => category.title.includes('All'))?.link;
    const filteredCategories = categories.filter((category) => !category.title.includes('All'));

    return (
        <div className={classes.categoriesList} data-test-id="categoryList">
            <MobileCreditBalance showBottomDivider showTopDivider contentCentered smallPadding />
            {displayHeader && (
                <div className={classes.wrapper}>
                    <div
                        className={classnames(classes.headerWrapper, {
                            [classes.extended]: readMore,
                        })}
                    >
                        <Breadcrumbs category={title} breadcrumbLabel={breadcrumbLabel} withSchema={false} />
                        <div className={classes.header}>
                            <div className={classes.titles}>
                                {shortDescription && (
                                    <div className={classes.description}>
                                        <RichText content={shortDescription} />
                                    </div>
                                )}
                            </div>
                            <div className={classes.tagsWrapperDesktop}>
                                <div className={classes.tagsWrapperTitle}>
                                    <p>Categories</p>
                                    <a href={allCategoryLink}>
                                        View All <img src={require('../../svg/arrow-orange.svg')} alt="" />
                                    </a>
                                </div>

                                <Tags categories={filteredCategories} title={title} />
                            </div>
                        </div>

                        <button className={classes.readMore} type="button" onClick={() => setReadMore(!readMore)}>
                            {readMore ? 'Read less' : 'Read more'}
                        </button>
                    </div>
                    <div>
                        <div className={classes.tagsWrapperMobile}>
                            <div className={classes.tagsWrapperTitle}>
                                <p>Categories</p>
                                <a href={allCategoryLink}>
                                    View All <img src={require('../../svg/arrow-orange.svg')} alt="" />
                                </a>
                            </div>
                            <Tags categories={filteredCategories} title={title} />
                        </div>
                        {brands?.length > 0 && <Carousel items={brands} mainTitle="Brands" categoryPageBlocks />}
                    </div>
                </div>
            )}
            <div>
                {printedSKUS || embroideredSKUS ? (
                    <FeaturedProducts
                        skus={
                            printedSKUS && embroideredSKUS
                                ? additionalSkuType === 'screenPrintedSkus'
                                    ? printedSKUS
                                    : embroideredSKUS
                                : printedSKUS || embroideredSKUS || []
                        }
                        listId={urlKey}
                        listName={name}
                        bestSellers={bestSellers}
                        title={displayFullView ? `Featured Products ${title}` : `Featured ${title}`}
                        tagsAggregation={tagsAggregation}
                        additionalSkuType={Boolean(printedSKUS && embroideredSKUS) ? additionalSkuType : undefined}
                        setAdditionalSkuType={setAdditionalSkuType}
                        isUpsells={displayFullView}
                    />
                ) : (
                    SKUS && (
                        <FeaturedProducts
                            skus={SKUS}
                            listId={urlKey}
                            listName={name}
                            bestSellers={bestSellers}
                            title={displayFullView ? `Featured Products ${title}` : `Featured ${title}`}
                            tagsAggregation={tagsAggregation}
                            isUpsells={displayFullView}
                            isSlider={!displayFullView}
                        />
                    )
                )}
            </div>
            {relatedCategories.length > 0 && (
                <div className={classes.categoriesForIndustry}>
                    <div className={classes.categoryNavLeft}>
                        <h2>Shop {title} For Your Industry</h2>
                    </div>
                    <ul className={classes.categoriesForIndustryListItems}>
                        <IndustryCategories categories={relatedCategories} />
                    </ul>
                </div>
            )}
            {categories.length > 0 && (
                <div className={classes.categories}>
                    <div className={classes.categoryNavLeft}>
                        <h2>{title} Categories</h2>
                    </div>
                    <ul className={classes.categoryListItems}>
                        {categories.map((category) => (
                            <ImageAndHoverText
                                title={category.title}
                                link={category.link}
                                image={category.image}
                                key={category.title}
                            />
                        ))}
                    </ul>
                </div>
            )}
            {faq?.length > 0 && <CategoryFaq title={title} faq={faq} />}
        </div>
    );
};
